import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'api';
import { TIMEFRAME } from 'dateUtils';
import { getSortedOptionsFromStrings } from 'utils';
import { FilterOption } from 'models/Filters';
import {
  SELLER_CONNECT_REVENUE_RANGE,
  SELLER_CONNECT_SELLER_FILTER_OPTION_FIELD,
} from 'models/SellerConnectEngine';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useMultiSort from 'hooks/useMultiSort';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import {
  COLUMNS,
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  SORTING_VALUES,
  SellersTableData,
} from '../components/SellersTable/data';
import { INTAKE_STATUS_LABELS, INTAKE_VIA_LABELS } from '../data';
import { AU_STATES } from 'models/DealRoom';

export default function useSellersFilters() {
  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const getSellerOptions = useCallback(async (search: string) => {
    const { data } =
      await api.sellerConnectEngine.getSellerConnectSellerFilterOptions(
        SELLER_CONNECT_SELLER_FILTER_OPTION_FIELD.NAME,
        search,
      );

    return getSortedOptionsFromStrings(data);
  }, []);

  const getBusinessOptions = useCallback(async (search: string) => {
    const { data } =
      await api.sellerConnectEngine.getSellerConnectSellerFilterOptions(
        SELLER_CONNECT_SELLER_FILTER_OPTION_FIELD.BUSINESS,
        search,
      );

    return getSortedOptionsFromStrings(data);
  }, []);

  const getIndustryOptions = useCallback(async (search: string) => {
    const { data } =
      await api.sellerConnectEngine.getSellerConnectSellerFilterOptions(
        SELLER_CONNECT_SELLER_FILTER_OPTION_FIELD.INDUSTRY,
        search,
      );

    return getSortedOptionsFromStrings(data);
  }, []);

  const sellerFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('seller'),
        SELLER_FILTER_KEY,
        SELLER_FILTER_KEY,
        getSellerOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getSellerOptions, t],
  );

  const businessFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('business'),
        BUSINESS_FILTER_KEY,
        BUSINESS_FILTER_KEY,
        getBusinessOptions,
      ) as FilterOption,
    [getBusinessOptions, getMultiSelectSearchableFilter, t],
  );

  const industryFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('industry'),
        INDUSTRY_FILTER_KEY,
        INDUSTRY_FILTER_KEY,
        getIndustryOptions,
      ) as FilterOption,
    [getIndustryOptions, getMultiSelectSearchableFilter, t],
  );

  const locationFilterOption = useMemo<FilterOption>(
    () => ({
      value: LOCATION_FILTER_KEY,
      label: t('location'),
      isMultiple: true,
      options: createSelectOptions(Object.values(AU_STATES)),
    }),
    [createSelectOptions, t],
  );

  const revenueFilterOption = useMemo<FilterOption>(
    () => ({
      value: REVENUE_FILTER_KEY,
      label: t('annualRevenue'),
      isMultiple: true,
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(SELLER_CONNECT_REVENUE_RANGE)),
    }),
    [createSelectOptions, t],
  );

  const intakeViaFilterOption = useMemo<FilterOption>(
    () => ({
      value: INTAKE_VIA_FILTER_KEY,
      label: t('intakeVia'),
      isMultiple: true,
      shouldSortOptions: false,
      options: Object.entries(INTAKE_VIA_LABELS).map(([value, label]) => ({
        value,
        label: t(label),
      })),
    }),
    [t],
  );

  const intakeStatusFilterOption = useMemo<FilterOption>(
    () => ({
      value: INTAKE_STATUS_FILTER_KEY,
      label: t('imStatus'),
      isMultiple: true,
      shouldSortOptions: false,
      options: Object.entries(INTAKE_STATUS_LABELS).map(([value, label]) => ({
        value,
        label: t(label),
      })),
    }),
    [t],
  );

  const dateFilterOption = useMemo<FilterOption>(
    () => ({
      value: DATE_FILTER_KEY,
      label: t('date'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME)),
    }),
    [createSelectOptions, t],
  );

  const filterOptions = useMemo(
    () => [
      sellerFilterOption,
      businessFilterOption,
      industryFilterOption,
      locationFilterOption,
      revenueFilterOption,
      intakeViaFilterOption,
      intakeStatusFilterOption,
      dateFilterOption,
    ],
    [
      sellerFilterOption,
      businessFilterOption,
      industryFilterOption,
      locationFilterOption,
      revenueFilterOption,
      intakeViaFilterOption,
      intakeStatusFilterOption,
      dateFilterOption,
    ],
  );

  const {
    multiSortValues: translatedSortOptions,
    multiSortDirectionTranslation,
  } = useMultiSort(
    COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(() => {
    const columnSortOptions = COLUMNS.reduce((acc, column) => {
      const sortValue = SORTING_VALUES[column];

      if (MULTI_SORT_DIRECTION_COPY[sortValue])
        acc[column] = {
          sortDirectionTranslation: multiSortDirectionTranslation[sortValue],
        };

      return acc;
    }, {} as Partial<Record<keyof SellersTableData, ColumnFilterOption>>);

    return {
      ...columnSortOptions,
      seller: { ...columnSortOptions.seller, options: sellerFilterOption },
      business: {
        ...columnSortOptions.business,
        options: businessFilterOption,
      },
      industry: {
        ...columnSortOptions.industry,
        options: industryFilterOption,
      },
      location: {
        ...columnSortOptions.location,
        options: locationFilterOption,
      },
      revenueRange: {
        ...columnSortOptions.revenueRange,
        options: revenueFilterOption,
      },
      intakeVia: {
        ...columnSortOptions.intakeVia,
        options: intakeViaFilterOption,
      },
      intakeStatus: {
        ...columnSortOptions.intakeStatus,
        options: intakeStatusFilterOption,
      },
      submittedAt: {
        ...columnSortOptions.submittedAt,
        options: dateFilterOption,
      },
    } as Partial<Record<keyof SellersTableData, ColumnFilterOption>>;
  }, [
    businessFilterOption,
    dateFilterOption,
    intakeStatusFilterOption,
    industryFilterOption,
    intakeViaFilterOption,
    locationFilterOption,
    multiSortDirectionTranslation,
    revenueFilterOption,
    sellerFilterOption,
  ]);

  return {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  };
}

export const SELLER_FILTER_KEY = 'name';
export const BUSINESS_FILTER_KEY = 'business';
export const INDUSTRY_FILTER_KEY = 'industry';
export const LOCATION_FILTER_KEY = 'state';
export const REVENUE_FILTER_KEY = 'revenueRange';
export const INTAKE_VIA_FILTER_KEY = 'mode';
export const INTAKE_STATUS_FILTER_KEY = 'intakeStatus';
export const DATE_FILTER_KEY = 'submittedTimeframe';
